import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useT } from "@/i18n";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Eye, MousePointerClick, TrendingUp, Wallet } from "lucide-react";

const RANGES = [7, 30, 90];

export function CampaignDetailDialog({ campaignId, onClose }) {
  const t = useT();
  const [days, setDays] = useState(30);
  const [tl, setTl] = useState(null);

  const load = useCallback(async () => {
    setTl(null);
    try { const { data } = await api.get(`/campaigns/${campaignId}/timeline?days=${days}`); setTl(data); }
    catch { setTl({ error: true }); }
  }, [campaignId, days]);

  useEffect(() => { load(); }, [load]);

  const chartMax = useMemo(() => {
    if (!tl?.series) return 1;
    return Math.max(1, ...tl.series.map((r) => Math.max(r.impressions, r.clicks)));
  }, [tl]);

  const c = tl?.campaign;
  const ctr = c && c.impressions > 0 ? ((c.clicks / c.impressions) * 100).toFixed(1) : "0.0";

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto" data-testid="ads-detail">
        <DialogHeader>
          <DialogTitle>{c?.title || t("ads.detail_title")}</DialogTitle>
          <DialogDescription>
            {c ? `${t(`ads.status_${c.status}`)} · ${t(`ads.billing_${c.billing_mode}`)}` : "…"}
          </DialogDescription>
        </DialogHeader>

        {!tl ? (
          <div className="space-y-3"><Skeleton className="h-20 rounded-xl" /><Skeleton className="h-40 rounded-xl" /></div>
        ) : tl.error ? (
          <p className="py-8 text-center text-sm text-muted-foreground">{t("ads.detail_not_found")}</p>
        ) : (
          <div className="space-y-4">
            {/* KPI strip */}
            <div className="grid grid-cols-4 gap-2">
              <Kpi icon={Eye} label={t("ads.impressions")} value={(c.impressions || 0).toLocaleString("de-DE")} />
              <Kpi icon={MousePointerClick} label={t("ads.clicks")} value={(c.clicks || 0).toLocaleString("de-DE")} />
              <Kpi icon={TrendingUp} label={t("ads.ctr")} value={`${ctr}%`} />
              <Kpi icon={Wallet} label={t("ads.total_spent")} value={`€${((c.spent_cents || 0) / 100).toFixed(2)}`} emphasis />
            </div>
            {c.budget_cents > 0 && (
              <p className="text-xs text-muted-foreground">{t("ads.spent_of", { spent: `€${((c.spent_cents || 0) / 100).toFixed(2)}`, total: `€${(c.budget_cents / 100).toFixed(0)}` })}</p>
            )}

            {/* Range picker */}
            <div className="flex gap-1.5">
              {RANGES.map((r) => (
                <button key={r} onClick={() => setDays(r)} data-testid={`ads-range-${r}`} className={`rounded-full px-3 py-1.5 text-xs font-medium ${days === r ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground"}`}>
                  {t("ads.range", { n: r })}
                </button>
              ))}
            </div>

            {/* Chart */}
            <div>
              <p className="mb-1.5 text-sm font-semibold text-foreground">{t("ads.chart_title")}</p>
              <div className="mb-2 flex gap-4 text-xs">
                <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-primary" /> {t("ads.impressions")}</span>
                <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: "#7C3AED" }} /> {t("ads.clicks")}</span>
              </div>
              <div className="overflow-x-auto">
                <div className="flex items-end gap-1" style={{ height: 140, minWidth: tl.series.length * 16 }}>
                  {tl.series.map((row) => (
                    <div key={row.date} className="flex flex-col items-center gap-1" style={{ minWidth: 14 }}>
                      <div className="flex items-end justify-center gap-0.5" style={{ height: 110 }}>
                        <div className="w-2 rounded-t bg-primary" style={{ height: `${Math.max(2, (row.impressions / chartMax) * 100)}%` }} />
                        <div className="w-2 rounded-t" style={{ height: `${Math.max(2, (row.clicks / chartMax) * 100)}%`, backgroundColor: "#7C3AED" }} />
                      </div>
                      <span className="text-[8px] text-muted-foreground">{row.date.slice(5)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Range totals */}
            <div>
              <p className="mb-1.5 text-sm font-semibold text-foreground">{t("ads.range_totals", { n: days })}</p>
              <div className="grid grid-cols-3 gap-2 text-center">
                <RangeStat label={t("ads.impressions")} value={(tl.total.impressions || 0).toLocaleString("de-DE")} />
                <RangeStat label={t("ads.clicks")} value={(tl.total.clicks || 0).toLocaleString("de-DE")} />
                <RangeStat label={t("ads.total_spent")} value={`€${((tl.total.spent_cents || 0) / 100).toFixed(2)}`} />
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Kpi({ icon: Icon, label, value, emphasis }) {
  return (
    <div className="rounded-xl bg-secondary/60 p-3 text-center">
      <Icon className={`mx-auto h-4 w-4 ${emphasis ? "text-primary" : "text-muted-foreground"}`} />
      <p className={`mt-1 font-display text-base font-semibold ${emphasis ? "text-primary" : "text-foreground"}`}>{value}</p>
      <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</p>
    </div>
  );
}

function RangeStat({ label, value }) {
  return (
    <div className="rounded-xl border border-border p-3">
      <p className="font-display text-base font-semibold text-foreground">{value}</p>
      <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</p>
    </div>
  );
}
